import React, { Fragment, useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import Skeleton from "@mui/material/Skeleton";
import NewsBlock from "../components/News/NewsLine";

import BlockTitle from "../components/Common/BlockTitle";
import PageHeading, { VisuallyHiddenHeading } from "../components/Common/PageHeading";
import TransparentButton from "../components/Common/Buttons";
import theme from "../styles/theme";
import Archive from "../components/Archive/Slider";
//import { loadVideosByIds, loadStreams } from "../utils/googleApi/youtube";
import { loadStreams } from "../utils/googleApi/youtube";
import { useHighlightsContext } from "../contexts/highlights";
import VideosSEO from "../components/SEO/VideosSEO";

import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";

const IconContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  z-index: 2;
  transition: background-color 0.2s ease;
  display: none;
  margin-left: auto;
  margin-right: auto;
`;

const VideosContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr;
  gap: 16px;
  margin-left: auto;
  margin-right: auto;
  transition: all 0.3s linear;

  @media (max-width: 1100px) {
    grid-template-columns: 1fr 1fr 1fr;
    & > *:nth-child(n + 4) {
      display: none;
    }
  }

  @media (max-width: 580px) {
    grid-template-columns: 1fr 1fr;
    & > *:nth-child(n + 3) {
      display: none;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;

    & > *:nth-child(n + 2) {
      display: none;
    }
  }
`;

const StreamsContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 16px;
  margin-left: auto;
  margin-right: auto;
  transition: all 0.3s linear;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
    & > *:nth-child(n + 3) {
      display: none;
    }
  }

  @media (max-width: 480px) {
    grid-template-columns: 1fr;

    & > *:nth-child(n + 2) {
      display: none;
    }
  }
`;

const VideoPost = styled.div`
  height: 100%;
  width: 100%;
  box-shadow: #00000040 0px 2px 8px 0px;
  color: ${theme.colors.white};
  flex: 1;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  background-color: ${theme.colors.darkLight};
  cursor: pointer;

  &:hover {
    ${IconContainer} {
      display: flex;
      background-color: #00000020;
    }
  }
`;

const StreamPost = styled.div`
  height: 100%;
  width: 100%;
  box-shadow: #00000040 0px 2px 8px 0px;
  color: ${theme.colors.white};
  flex: 1;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  border-radius: 4px;
  overflow: hidden;
  background-color: ${theme.colors.darkLight};
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.secondaryDark};
  }

  &:hover img {
    transform: scale(1.05);
  }
`;

const VideoContainer = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
`;

const VideoImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 100%;
  transition: transform 0.3s;
`;

const VideoTitleContainer = styled.div`
  padding: 4px 16px;
`;

const StreamTitleContainer = styled.div`
  padding: 20px 16px;
`;

const Title = styled.div`
  font-size: 16px;
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
`;

const Date = styled.div`
  font-family: "Montserrat", sans-serif;
  font-size: 12px;
  opacity: 0.5;
`;

const StyledLink = styled(Link)`
  cursor: default;
  text-decoration: none;
  color: inherit;
`;

const VideoPostComponent = ({ link, image, title, date }) => {
  return (
    <StyledLink to={link} target="_blank" rel="noopener noreferrer">
      <VideoPost>
        <VideoContainer>
          <VideoImage src={image} />
          <IconContainer>
            <PlayCircleOutlineIcon sx={{ fontSize: 98, color: "#878787" }} />
          </IconContainer>
        </VideoContainer>
      </VideoPost>
    </StyledLink>
  );
};

const StreamPostComponent = ({ link, image, title, date }) => {
  return (
    <StyledLink to={link} target="_blank" rel="noopener noreferrer">
      <StreamPost>
        <VideoContainer>
          <VideoImage src={image} />
        </VideoContainer>
        <StreamTitleContainer>
          <Date>{date}</Date>
          <Title>{title}</Title>
        </StreamTitleContainer>
      </StreamPost>
    </StyledLink>
  );
};

const VideoPostSkeleton = () => {
  return (
    <VideoPost>
      <VideoContainer>
        <Skeleton animation="wave" variant="rectangular" width="100%" height="100%" />
      </VideoContainer>
      <VideoTitleContainer>
        <Skeleton width="60%" />
        <Skeleton />
      </VideoTitleContainer>
    </VideoPost>
  );
};

export const StreamsBlock = () => {
  const [streams, setStreams] = useState([]);

  const loadStreamsVideos = async () => {
    const streams = await loadStreams();
    setStreams(streams);
  };

  useEffect(() => {
    loadStreamsVideos();
  }, []);

  return (
    <>
      <BlockTitle>
        <PageHeading as="h2">Трансляції</PageHeading>
        <StyledLink to="/videos/streams">
          <TransparentButton>Переглянути все</TransparentButton>
        </StyledLink>
      </BlockTitle>
      <StreamsContainer>
        {streams.length
          ? streams.slice(0, 3).map((item, key) => <StreamPostComponent {...item} />)
          : [1, 2, 3].map((item) => <VideoPostSkeleton />)}
      </StreamsContainer>
    </>
  );
};

export const HighlightsBlock = () => {
  const { seasonHightlights, loadSeasonHighlights } = useHighlightsContext();
  const seasonId = 11;

  const last4Hightlights = useMemo(() => {
    const last4 = seasonHightlights[seasonId]?.slice(0, 4) || [];
    if (last4.length < 4) {
      const last4prev = seasonHightlights[seasonId - 1] || [];
      return [...last4, ...last4prev.reverse()].slice(0, 4);
    }
    return last4;
  }, [seasonId, seasonHightlights]);

  useEffect(() => {
    loadSeasonHighlights(seasonId);
    loadSeasonHighlights(seasonId - 1);
    // eslint-disable-next-line
  }, [seasonId]);

  return (
    <>
      <BlockTitle>
        <PageHeading as="h2">Огляди</PageHeading>
        <StyledLink to="/videos/seasons/10">
          <TransparentButton>Переглянути все</TransparentButton>
        </StyledLink>
      </BlockTitle>
      <VideosContainer>
        {last4Hightlights.length
          ? last4Hightlights.map((item, key) => <VideoPostComponent {...item} />)
          : [1, 2, 3, 4].map((item) => <VideoPostSkeleton />)}
      </VideosContainer>
    </>
  );
};

const Videos = () => {
  // const [conversations, setConversations] = useState([]);
  const currentUrl = `https://slickracing.com.ua/videos`;

  // const loadConversations = async () => {
  //   const videos = await loadVideosByIds(["Lxzh7mDZi04", "_OzwnG9KT24", "cI_TsUqzDRU"]);
  //   setConversations(videos);
  // };

  useEffect(() => {
    //loadConversations();
  }, []);

  return (
    <Fragment>
      <VideosSEO url={currentUrl} />
      <VisuallyHiddenHeading>Відео: огляди, трансляції та інтерв'ю</VisuallyHiddenHeading>

      <div style={{ marginTop: 24 }}>
        <HighlightsBlock />
      </div>
      <div style={{ marginTop: 24 }}>
        <NewsBlock />
      </div>
      <div style={{ marginTop: 24 }}>
        <StreamsBlock />
      </div>
      {/* <div style={{ marginTop: 48 }}>
        <BlockTitle>
          Інтерв'ю
          <StyledLink to="/videos/conversations">
            <TransparentButton>Переглянути все</TransparentButton>
          </StyledLink>
        </BlockTitle>
        <PostsContainer>
          {conversations.length
            ? conversations.map((item, key) => <VideoPostComponent {...item} />)
            : [1, 2, 3].map((item) => <VideoPostSkeleton />)}
        </PostsContainer>
      </div> */}
      <div style={{ marginTop: 24 }}>
        <Archive />
      </div>
    </Fragment>
  );
};

export default Videos;
